import React from "react";

export default function CustomerServicesByNameApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Customer Services API — Fetch Services by Customer Name
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Retrieve the list of services available to a customer using the
            customer name instead of the internal LiveTakeoff customer ID. This
            endpoint is intended for integrations such as Portside where the
            external system may only know the customer name.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Method
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">GET</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900 break-all">
                /api/customer-services/by-name/
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            This endpoint finds a customer by exact customer name and returns
            that customer’s mapped services. If the customer has no service
            mappings, the endpoint returns the default list of all services.
          </p>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              Use this endpoint when the caller does not have the LiveTakeoff{" "}
              <code className="font-mono">customer_id</code>.
            </li>
            <li>
              The customer name is passed as a query parameter and should be URL
              encoded.
            </li>
          </ul>
        </section>

        {/* Request */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Request
          </h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">HTTP</div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <span className="font-semibold">Method:</span>{" "}
                  <code className="font-mono">GET</code>
                </li>
                <li>
                  <span className="font-semibold">URL:</span>{" "}
                  <code className="font-mono">
                    /api/customer-services/by-name/
                  </code>
                </li>
                <li>
                  <span className="font-semibold">Headers:</span>
                  <br />
                  <code className="font-mono">
                    Authorization: JWT your_token
                  </code>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Query parameters
              </div>
              <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">customer_name</code>{" "}
                  <span className="ml-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700">
                    required
                  </span>{" "}
                  — <span className="font-mono">string</span>. Exact customer
                  name. Spaces and special characters must be URL encoded.
                </li>
              </ul>
            </div>
          </div>

          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  GET
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                /api/customer-services/by-name/
              </span>
            </div>

            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`GET https://api-livetakeoff.herokuapp.com/api/customer-services/by-name/?customer_name=Aviation%20Support%20Florida
Authorization: JWT your_access_token`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Response */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Response
          </h2>

          <div className="mt-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Envelope
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                The endpoint returns the matched customer and an array of
                service objects.
              </p>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">customer</code> — matched customer
                  object
                </li>
                <li>
                  <code className="font-mono">services</code> — array of
                  services available for the customer
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-6">
              <div className="text-sm font-semibold text-slate-900">
                Service object fields
              </div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">id</code> —{" "}
                  <span className="font-mono">number</span>
                </li>
                <li>
                  <code className="font-mono">name</code> —{" "}
                  <span className="font-mono">string</span>
                </li>
                <li>
                  <code className="font-mono">short_name</code> —{" "}
                  <span className="font-mono">string</span>
                </li>
                <li>
                  <code className="font-mono">short_description</code> —{" "}
                  <span className="font-mono">string</span>
                </li>
                <li>
                  <code className="font-mono">description</code> —{" "}
                  <span className="font-mono">string</span>
                </li>
                <li>
                  <code className="font-mono">category</code> —{" "}
                  <span className="font-mono">string</span>
                </li>
                <li>
                  <code className="font-mono">checklist_actions</code> —{" "}
                  <span className="font-mono">array</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-6">
            <div className="text-sm font-semibold text-slate-900">
              Service category mapping
            </div>

            <p className="mt-2 text-sm leading-6 text-slate-700">
              The <code className="font-mono">category</code> field uses a
              compact internal code to identify the service category type.
            </p>

            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      Code
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      Meaning
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white">
                  <tr>
                    <td className="px-4 py-3">
                      <code className="font-mono text-slate-900">I</code>
                    </td>
                    <td className="px-4 py-3 text-slate-700">Interior</td>
                  </tr>

                  <tr>
                    <td className="px-4 py-3">
                      <code className="font-mono text-slate-900">E</code>
                    </td>
                    <td className="px-4 py-3 text-slate-700">Exterior</td>
                  </tr>

                  <tr>
                    <td className="px-4 py-3">
                      <code className="font-mono text-slate-900">O</code>
                    </td>
                    <td className="px-4 py-3 text-slate-700">Other</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Response
              </h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  200
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                application/json
              </span>
            </div>

            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`{
  "customer": {
    "id": 123,
    "name": "Aviation Support Florida"
  },
  "services": [
    {
      "id": 101,
      "name": "Full Exterior Detail",
      "short_name": "Exterior Detail",
      "short_description": "Short description of Full Exterior Detail",
      "description": "Description of Full Exterior Detail",
      "category": "E",
      "is_exterior_detail_level_2": false,
      "is_exterior_wet_wash": true,
      "checklist_actions": [
        {
          "id": 10,
          "name": "Clean windshield"
        }
      ]
    }
  ]
}`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Usage snippets */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Usage examples
          </h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-1">
            <div className="not-prose my-2 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  curl
                </h3>
              </div>

              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    GET
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">
                  /api/customer-services/by-name/
                </span>
              </div>

              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`curl -X GET "https://api.livetakeoff.com/api/customer-services/by-name/?customer_name=Aviation%20Support%20Florida" \\
  -H "Authorization: JWT your_access_token"`}
                  </pre>
                </div>
              </div>
            </div>

            <div className="not-prose my-2 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  fetch (JavaScript)
                </h3>
              </div>

              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    GET
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">
                  /api/customer-services/by-name/
                </span>
              </div>

              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`const customerName = "Aviation Support Florida";

const res = await fetch(
  "https://api.livetakeoff.com/api/customer-services/by-name/?customer_name=" +
    encodeURIComponent(customerName),
  {
    method: "GET",
    headers: {
      "Authorization": "JWT " + token
    }
  }
);

const data = await res.json();`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Validation / rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Validation & rules
          </h2>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              <code className="font-mono">customer_name</code> is required.
            </li>
            <li>Customer lookup should use an exact case-insensitive match.</li>
            <li>
              Spaces and special characters should be URL encoded using{" "}
              <code className="font-mono">encodeURIComponent</code>.
            </li>
            <li>
              If no matching customer is found, the endpoint returns{" "}
              <code className="font-mono">404</code>.
            </li>
            <li>
              Results are ordered by <code className="font-mono">name</code>.
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
